import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

import useDispatch from 'client/hooks/useDispatch';
import { SR_API_ACTION_UTILITY_PARTNER_WITHDRAW_PENDING_BALANCE } from 'api/utilityActionTypes';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UPPartnerPayoutFlowConnectBankStep from 'client/components/UPPartnerPayoutFlow/UPPartnerPayoutFlowConnectBankStep';
import UPPartnerPayoutFlowTransferInitiatedStep from 'client/components/UPPartnerPayoutFlow/UPPartnerPayoutFlowTransferInitiatedStep';

import UtilityPayoutActions from 'client/actions/UtilityPayout';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

const UP_PAYOUT_FLOW_STEP_CONNECT_BANK = 'CONNECT_BANK';
const UP_PAYOUT_FLOW_STEP_TRANSFER_INITIATED = 'TRANSFER_INITIATED';

const UPPartnerPayoutFlow = ({
  utilityPartner,
  utilityPayouts = [],
  utilityPartnerStripeAccount,
  onClose,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(UP_PAYOUT_FLOW_STEP_CONNECT_BANK);
  const [isLoading, setIsLoading] = useState(false);
  const [withdrawErrorMessage, setWithdrawErrorMessage] = useState(null);

  const pendingPayoutBalance = utilityPayouts.reduce((acc, payout) => acc + payout.amount, 0);
  const connectBankPageUrl = `/utility-profit/${utilityPartner.slug}/connect-bank`;

  const handleSuccessfulTransfer = () => {
    const promises = [
      dispatch(UtilityPayoutActions.getPendingPayoutsByPartnerSlug, {
        slug: utilityPartner.slug,
        forceRefresh: true,
      }),
      dispatch(UtilityPartnerActions.getRevenueDetails, {
        slug: utilityPartner.slug,
        forceRefresh: true,
      }),
    ];

    return Promise.all(promises).then(() => {
      setIsLoading(false);
      return setCurrentStep(UP_PAYOUT_FLOW_STEP_TRANSFER_INITIATED);
    });
  };

  const onWithdrawPendingBalance = () => {
    setIsLoading(true);
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_WITHDRAW_PENDING_BALANCE,
      payload: {
        utilityPartnerId: utilityPartner.id,
        utilityStripeAccountId: utilityPartner.utilityStripeAccountId,
      },
      onSuccess: handleSuccessfulTransfer,
      onError: (err) => {
        setWithdrawErrorMessage(err.message);
        setIsLoading(false);
      },
    });
  };

  const onContinueTransfer = () => {
    if (!utilityPartner.utilityStripeAccountId) {
      return history.push(connectBankPageUrl);
    }
    return onWithdrawPendingBalance();
  };

  switch (currentStep) {
    case UP_PAYOUT_FLOW_STEP_CONNECT_BANK:
      return (
        <UPPartnerPayoutFlowConnectBankStep
          utilityPartner={utilityPartner}
          currentPendingBalance={pendingPayoutBalance}
          withdrawErrorMessage={withdrawErrorMessage}
          utilityPartnerStripeAccount={utilityPartnerStripeAccount}
          isLoading={isLoading}
          onContinueTransfer={onContinueTransfer}
          onClose={onClose}
        />
      );
    case UP_PAYOUT_FLOW_STEP_TRANSFER_INITIATED:
      return <UPPartnerPayoutFlowTransferInitiatedStep onClose={onClose} />;
    default:
      return null;
  }
};

UPPartnerPayoutFlow.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  utilityPayouts: PropTypes.array,
  utilityPartnerStripeAccount: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

UPPartnerPayoutFlow.displayName = 'UPPartnerPayoutFlow';

export default UPPartnerPayoutFlow;
